import { Info, UploadFile } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { blue } from '@mui/material/colors'
import { AxiosRequestConfig } from 'axios'
import jsPDF from 'jspdf'
import React, { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Controller, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { translateVariablePlaceholder } from '../../../domain/Customer/TemplateVariables'
import { AdoptersClient } from '../../../interactors/clients/AdoptersClient'
import { backendClient } from '../../../interactors/clients/client'
import {
  AdopterDto,
  AdoptionAttemptDto,
  AnimalAdoptionDtoOfferType,
  AnimalDto,
  AnimalDtoSex,
  AnimalDtoSpecies,
  CreateAnimalBodyDtoVaccinationStatus,
  CustomerDto,
  CustomerPdfTemplateDto,
} from '../../../interactors/gen/backendClient'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { blobToFile } from '../../../utils/blobToFile'
import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import { setOpacity } from '../../../utils/setOpacity'
import { ControlledAutoCompleteWithCategories } from '../../common/ControlledAutocompleteWithCategories'
import { InfoBox } from '../../common/InfoBox'
import { colors, spacingItem, theme } from '../../theme'
import { AnimalsClient } from '../../../interactors/clients/AnimalsClient'
import { ControlledDateField } from '../../common/ControlledDateField'
import { isValidPastDateCoherence } from '../../../utils/date/isValidPastDateCoherence'
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input'
import { ControlledSelectField } from '../../common/ControlledSelectField'
import {
  getColorForAnimalVaccineStatus,
  animalAdoptionOfferTypeOrder,
  getReadableAnimalAdoptionOfferType,
  getReadableAnimalVaccineStatus,
  getReadableSpecies,
} from '../../../domain/Animal/AnimalDisplay'
import { ColoredChipSelectField } from '../../common/ColoredChipSelectField'
import { catBreeds, dogBreeds } from '../../../domain/Animal/Breeds'
import { ControlledAutoCompleteWithCustomValue } from '../../common/ControlledAutocompleteWithCustomValue'
import { Link } from 'react-router-dom'
import { useAccountsStore } from '../../../store/AccountsStore'
import dayjs from 'dayjs'
import { addPrimaryFabSx, addSecondaryFabSx } from '../../common/addPrimaryCtaSx'

interface Props {
  setAdoptionAttempt: React.Dispatch<React.SetStateAction<AdoptionAttemptDto | undefined>>
  type: 'certificate' | 'contract'
  adopter: AdopterDto
  animal: AnimalDto
  adoptionAttemptId: string
  association?: CustomerDto | null
  specie?: AnimalDtoSpecies
  isLocked?: boolean
  disabled?: boolean
  buttonLabel?: string
  manualImportMode?: boolean
}

export interface VariableFormType {
  '{{adopter_address}}': string | undefined
  '{{adopter_city}}': string | undefined
  '{{adopter_email}}': string | undefined
  '{{adopter_first_name}}': string | undefined
  '{{adopter_last_name}}': string | undefined
  '{{adopter_phone}}': string | undefined
  '{{adopter_postal_code}}': string | undefined
  '{{adoption_date}}': string | undefined
  '{{adoption_fee}}': string | undefined
  '{{adoption_type}}': string | undefined
  '{{deposit_amount}}': string | undefined
  '{{deposit_status}}': string | undefined
  '{{deposit_date}}': string | undefined
  '{{animal_birth_date}}': string | undefined
  '{{animal_breed}}': string | undefined
  '{{animal_gender}}': string | undefined
  '{{animal_identification_number}}': string | undefined
  '{{animal_name}}': string | undefined
  '{{animal_new_name}}': string | undefined
  '{{animal_color}}': string | undefined
  '{{current_date}}': string | undefined
  '{{vaccine_recall_date}}': string | undefined
  '{{vaccine_status}}': string | undefined
  '{{sterilization_date}}': string | undefined
  '{{deworming_recall_date}}': string | undefined
  '{{flea_control_recall_date}}': string | undefined
  '{{identifying_marks}}': string | undefined
  '{{animal_species}}': string | undefined
  '{{asso_name}}': string | undefined
  '{{asso_address}}': string | undefined
  '{{asso_postal_code}}': string | undefined
  '{{asso_city}}': string | undefined
  '{{asso_siret_number}}': string | undefined
  '{{president_last_name}}': string | undefined
  '{{president_first_name}}': string | undefined
  '{{asso_logo}}': string | undefined
  '{{asso_signature}}': string | undefined
  '{{sterilization_status}}': string | undefined
  '{{asso_comment}}': string | undefined
  '{{sterilization_deposit_amount}}': string | undefined
  '{{date_7_days}}': string | undefined
  '{{custom_contract_date}}': string | undefined
  '{{custom_certificate_date}}': string | undefined
  '{{vaccine_last_date}}': string | undefined
  '{{deworming_last_date}}': string | undefined
  '{{flea_control_last_date}}': string | undefined
  '{{family_firstName}}': string | undefined
  '{{family_lastName}}': string | undefined
}

const moneyVariableKeys: Array<keyof VariableFormType> = [
  '{{adoption_fee}}',
  '{{deposit_amount}}',
  '{{sterilization_deposit_amount}}',
]

const normalizeMoneyVariableValue = (value: VariableFormType[keyof VariableFormType]): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  const originalValue = String(value).trim()
  const normalizedValue = originalValue.replace(/\s/g, '').replace(/€/g, '').replace(',', '.')
  const numericValue = normalizedValue.match(/^\d+(?:\.\d+)?/)?.[0]

  return numericValue || originalValue
}

const normalizeVariableFormValues = (values: Partial<VariableFormType>): Partial<VariableFormType> => {
  const normalizedValues = { ...values }
  const depositStatus = normalizedValues['{{deposit_status}}']

  if (typeof depositStatus === 'boolean') {
    normalizedValues['{{deposit_status}}'] = depositStatus ? 'true' : 'false'
  }

  moneyVariableKeys.forEach((key) => {
    normalizedValues[key] = normalizeMoneyVariableValue(normalizedValues[key])
  })

  return normalizedValues
}

const mergeDefinedVariableFormValues = (
  currentValues: Partial<VariableFormType>,
  nextValues: Partial<VariableFormType>
): Partial<VariableFormType> => {
  return Object.entries(nextValues).reduce<Partial<VariableFormType>>(
    (acc, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return acc
      }

      acc[key as keyof VariableFormType] = value as VariableFormType[keyof VariableFormType]
      return acc
    },
    { ...currentValues }
  )
}

const materializeTemplateVariableFormValues = (
  templateKeys: string[],
  currentValues: Partial<VariableFormType>
): Partial<VariableFormType> => {
  return templateKeys.reduce<Partial<VariableFormType>>(
    (acc, key) => {
      if (!(key in acc)) {
        acc[key as keyof VariableFormType] = undefined
      }

      return acc
    },
    { ...currentValues }
  )
}

const getTemplateVariableKeys = (
  values: Partial<VariableFormType> | null | undefined,
  missingData: string[] = [],
  declaredTemplateKeys: string[] = []
): string[] => {
  return Array.from(new Set([...declaredTemplateKeys, ...Object.keys(values ?? {}), ...missingData]))
}

const getDeclaredTemplateVariableKeys = (templates: Array<CustomerPdfTemplateDto | null | undefined>): string[] => {
  return Array.from(
    new Set(
      templates
        .flatMap((template) => template?.variables ?? [])
        .map((zone) => zone.variable)
        .filter(Boolean)
    )
  )
}

type DepositStatusFieldValue = 'true' | 'false' | undefined

const toDepositStatusAttemptValue = (value?: string | boolean | null, fallback?: boolean): boolean | undefined => {
  if (value === true || value === 'true') {
    return true
  }

  if (value === false || value === 'false') {
    return false
  }

  return fallback
}

const toDepositStatusFieldValue = (value?: string | boolean | null, fallback?: boolean): DepositStatusFieldValue => {
  const normalizedValue = toDepositStatusAttemptValue(value, fallback)

  if (normalizedValue === true) {
    return 'true'
  }

  if (normalizedValue === false) {
    return 'false'
  }

  return undefined
}

export const AddAdoptionDocumentModal: React.FC<Props> = ({
  setAdoptionAttempt,
  type,
  adopter,
  adoptionAttemptId,
  animal,
  association,
  specie,
  disabled = false,
  isLocked = false,
  buttonLabel,
  manualImportMode = false,
}) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const globalSnackbar = useGlobalSnackbarStore()
  const { getRootProps, getInputProps, isDragActive, acceptedFiles, inputRef } = useDropzone({
    multiple: false,
    accept: manualImportMode
      ? {
          'application/pdf': ['.pdf'],
          'image/png': ['.png'],
          'image/jpeg': ['.jpg', '.jpeg'],
        }
      : {
          'application/pdf': ['.pdf'],
        },
    onDropRejected: () => {
      globalSnackbar.triggerErrorMessage(
        manualImportMode ? `Veuillez importer un document PDF, PNG ou JPEG.` : `Veuillez importer un document PDF.`
      )
    },
  })
  const [step, setStep] = React.useState(0)
  const [loading, setLoading] = useState(false)
  const [choosedPetsoDocument, setChoosedPetsoDocument] = useState<boolean>(false)
  const [fileUrl, setFileUrl] = useState<string>('')
  const [missingData, setMissingData] = useState<string[]>([])
  const [allVariables, setAllVariables] = useState<Partial<VariableFormType> | null>()

  const adopterId = adopter.id
  const animalId = animal.id

  /*   const previewCertificateURLs = {
    cat: 'https://petso-customers-documents.s3.amazonaws.com/Certificat-Petso-Chat.pdf',
    dog: 'https://petso-customers-documents.s3.amazonaws.com/Certificat-Petso-Chien.pdf',
    ferret: 'https://petso-customers-documents.s3.amazonaws.com/Certificat-Petso-Furet.pdf',
    rabbit: 'https://petso-customers-documents.s3.amazonaws.com/Certificat-Petso-Lapin.pdf',
  } */

  const sxField = { ...spacingItem }

  const accountsStore = useAccountsStore()

  const resetAndCloseDialog = () => {
    setStep(manualImportMode ? 1 : 0)
    setFileUrl('')
    setChoosedPetsoDocument(false)
    setMissingData([])
    setAllVariables(null)
    acceptedFiles.length = 0
    acceptedFiles.splice(0, acceptedFiles.length)
    if (inputRef.current?.value) {
      inputRef.current.value = ''
    }
    setLoading(false)
    closeDialog()
  }

  const adoptionAttempt = adopter.adoptionAttempts?.find((attempt) => attempt.id === adoptionAttemptId)

  const specieThatHasCertificateGeneration =
    specie && ['cat', 'dog', 'ferret', 'rabbit'].includes(specie)
      ? (specie as 'cat' | 'dog' | 'ferret' | 'rabbit')
      : null

  const mutation = useMutation(
    async ({ file, pdfTemplateKey }: { file?: File; pdfTemplateKey?: string }) => {
      const formData = new FormData()

      if (file) {
        formData.append('file', file)
      }

      if (manualImportMode && file) {
        return await AdoptersClient.addSignedCertificateOrContractToAdoptionAttempt(
          adopterId,
          adoptionAttemptId,
          type,
          formData
        )
      }

      if (pdfTemplateKey) {
        formData.append('pdfTemplateKey', pdfTemplateKey)
        formData.append('deliveredByHand', 'false')
      }

      return await AdoptersClient.addOriginalDocumentToAdoptionAttempt(adopterId, adoptionAttemptId, formData, type)
    },
    {
      onSuccess: (data) => {
        setAdoptionAttempt(data)
        adopter.adoptionAttempts = adopter.adoptionAttempts?.map((a) => (a.id === adoptionAttemptId ? data : a))
        resetAndCloseDialog()
        globalSnackbar.triggerSuccessMessage(`Votre document a bien été ajouté !`)
      },
      onError: (error: any) => {
        const errorMessage = Array.isArray(error?.response?.data?.message)
          ? error?.response?.data?.message?.[0]
          : error?.response?.data?.message

        globalSnackbar.triggerErrorMessage(
          errorMessage ||
            (manualImportMode
              ? `Impossible d'importer le document. Vérifiez qu'il s'agit d'un PDF, PNG ou JPEG puis réessayez.`
              : `Impossible d'importer le document. Vérifiez qu'il s'agit d'un PDF puis réessayez.`)
        )
      },
    }
  )

  const generateCertificateMutation = useMutation(
    async () => {
      return await AdoptersClient.generateCertificate(animalId)
    },
    {
      onSuccess: (data) => {
        acceptedFiles[0] = blobToFile(data, 'Certificat by Petso.pdf')
        setFileUrl(URL.createObjectURL(data))
        setChoosedPetsoDocument(true)
        setStep(1)
        globalSnackbar.triggerSuccessMessage(`Votre certificat a bien été généré !`)
      },
    }
  )

  const convertHtmlToPdf = async (templateKey: string) => {
    setLoading(true)
    try {
      const config: AxiosRequestConfig = {
        method: 'GET',
        url: `/adopters/generate/template/${animalId}?adopterId=${adopterId}&templateKey=${templateKey}`,
        responseType: 'json', // Expect JSON response
      }

      const response = await backendClient(config)

      // Access the JSON response directly
      const htmlContent = response.data.htmlContent
      const missingData = response.data.missingData
      const allVariables = normalizeVariableFormValues(response.data.allVariables)
      if (typeof allVariables['{{adoption_fee}}'] === 'string') {
        allVariables['{{adoption_fee}}'] = allVariables['{{adoption_fee}}'].replace('€', '').trim()
      }
      setAllVariables((prev) => {
        return mergeDefinedVariableFormValues(prev ?? {}, allVariables)
      })
      setMissingData((prev) => [...prev, ...missingData])

      const pdf = new jsPDF('p', 'pt', 'a4') // Use points (pt) for dimensions

      // Create a container element to inject the HTML content
      const container = document.createElement('div')
      container.style.width = '565px' // Increase width to fill more of the page
      container.style.margin = '0 auto' // Center the content
      container.innerHTML = htmlContent

      // Improve HTML/CSS for PDF rendering
      container.style.fontSize = '10pt' // Slightly larger font size for better readability
      container.style.lineHeight = '1.3' // Adjust line height to prevent overlap
      container.style.overflow = 'hidden' // Prevent overflow
      container.querySelectorAll('ul').forEach((ul) => {
        ul.style.paddingInlineStart = '20px' // Adjust list indentation
      })
      container.querySelectorAll('li').forEach((li) => {
        li.style.paddingLeft = '10px' // Adjust list item indentation
      })

      // Add the HTML content to the PDF
      await pdf.html(container, {
        callback: (doc) => {
          // Get the PDF as a Blob
          const pdfBlob = doc.output('blob')
          acceptedFiles[0] = blobToFile(pdfBlob, 'Certificat by Petso.pdf')

          setFileUrl(URL.createObjectURL(pdfBlob))
          setChoosedPetsoDocument(true)
          setLoading(false)
        },
        margin: [40, 40, 40, 40], // Set margins in points (20pt ~ 7mm)
        autoPaging: 'text', // Adjust paging based on text content
        html2canvas: {
          proxy: 'https://petso-backend.herokuapp.com/v1/proxy/html2canvas', // Use a proxy to avoid CORS issues
          allowTaint: false,
          useCORS: false,
          letterRendering: true,
          logging: false,
          scale: 0.9, // Return to default scale for better content fit
        },
        width: 565, // Adjusted content width
      })
    } catch (error) {
      console.error('Error converting HTML to PDF:', error)
      setLoading(false)
    }
  }

  const fillPdfTemplate = async (templateId: string) => {
    setLoading(true)
    try {
      const config: AxiosRequestConfig = {
        method: 'GET',
        url: `/adopters/generate/pdf-template/${animalId}?adopterId=${adopterId}&templateId=${templateId}`,
        responseType: 'arraybuffer', // Expect binary PDF file
      }

      const response = await backendClient(config)

      // Extract metadata from headers
      const metadataHeader = response.headers['x-metadata'] // Adjust for the exact header name
      console.log('Headers', response.headers)
      let metadata: { missingData: string[]; allVariables: VariableFormType } | undefined
      if (metadataHeader) {
        // Decode Base64 string
        const decodedString = atob(metadataHeader)
        metadata = JSON.parse(decodedString)
        console.log('Metadata:', metadata)
      }

      const { missingData, allVariables } = metadata ?? { missingData: [], allVariables: {} as VariableFormType }
      const normalizedAllVariables = normalizeVariableFormValues(allVariables)

      // Update state
      setAllVariables((prev) => {
        return mergeDefinedVariableFormValues(prev ?? {}, normalizedAllVariables)
      })
      setMissingData(missingData)

      // Convert binary PDF to Blob for rendering or download
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      console.log('Generated PDF URL:', pdfUrl)
      acceptedFiles[0] = blobToFile(blob, 'Certificat by Petso.pdf')
      setFileUrl(pdfUrl)
      setChoosedPetsoDocument(true)
      setLoading(false)

      // Use `pdfUrl` for rendering or downloading
      console.log('Generated PDF URL:', pdfUrl)
    } catch (error) {
      console.error('Error converting HTML to PDF:', error)
      setLoading(false)
    }
  }

  const onSubmit = async (data?: formType) => {
    if (data && data.templateKey === 'Pas encore de modèles dans votre asso') return
    if (step === 0) {
      setLoading(true)
      if (data && data.templateKey === 'Importer un document PDF') {
        return setStep(1)
      }
      setChoosedPetsoDocument(true)
      if (data && data.templateKey === 'Certificat généré par Petso' && type === 'certificate')
        await generateCertificateMutation.mutateAsync()
      if (data && data.templateKey === 'Contrat généré par Petso' && type === 'contract')
        await convertHtmlToPdf('C-1002-H2lmUwJMPpx3otc6zYjlX.html')
      if (data && data.templateKey) {
        const documentKey = association?.templates?.find((template) => template.name === data.templateKey)?.documentKey
        if (documentKey) await convertHtmlToPdf(documentKey)
      }
      if (data && data.templateKey) {
        const pdfTemplate = association?.pdfTemplates?.find((template) => template.name === data.templateKey)
        if (pdfTemplate) {
          if (pdfTemplate.variables && pdfTemplate.variables.length > 0) {
            await fillPdfTemplate(pdfTemplate.id)
          } else {
            const documentKey = pdfTemplate.documentKey
            if (documentKey) await mutation.mutateAsync({ pdfTemplateKey: documentKey })
          }
        }
      }
      setStep(1)
      return setLoading(false)
    }

    if (acceptedFiles[0]) {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl)
      }
      let templateKey
      if (watchTemplateKey) {
        const pdfTemplate = association?.pdfTemplates?.find((template) => template.name === watchTemplateKey)
        if (pdfTemplate) {
          templateKey = pdfTemplate.documentKey
        } else {
          templateKey = undefined
        }
      }
      await mutation.mutateAsync({ file: acceptedFiles[0], pdfTemplateKey: templateKey })
    } else {
      globalSnackbar.triggerErrorMessage(`Vous devez ajouter un fichier !`)
    }
  }

  const onSubmitVariableForm = async (data: VariableFormType) => {
    if (!adopter.adoptionAttempts) return
    setLoading(true)

    const adoptionAttempt = adopter.adoptionAttempts.find((attempt) => attempt.animalId === animal.id)
    if (!adoptionAttempt) return
    const isFreeAmount = animal.adoption.offerType === AnimalAdoptionDtoOfferType.FreeAmount

    const depositDate = data['{{deposit_date}}']
    const isDepositDateFilled = depositDate != null && depositDate !== ''
    const isDepositDateString = typeof depositDate === 'string'
    const isDepositDateLocalFormat = isDepositDateString && depositDate.includes('/')

    const customContractDate = data['{{custom_contract_date}}']
    const isCustomContractDateFilled = customContractDate != null && customContractDate !== ''
    const isCustomContractDateString = typeof customContractDate === 'string'
    const isCustomContractDateLocalFormat = isCustomContractDateString && customContractDate.includes('/')

    const customCertificateDate = data['{{custom_certificate_date}}']
    const isCustomCertificateDateFilled = customCertificateDate != null && customCertificateDate !== ''
    const isCustomCertificateDateString = typeof customCertificateDate === 'string'
    const isCustomCertificateDateLocalFormat = isCustomCertificateDateString && customCertificateDate.includes('/')

    adoptionAttempt.depositAmount = Number(data['{{deposit_amount}}']?.trim().replace(',', '.').replace('€', '')) || 0
    adoptionAttempt.depositDate = isDepositDateFilled
      ? dayjs(depositDate, isDepositDateLocalFormat ? 'DD/MM/YYYY' : undefined).toISOString()
      : ''
    adoptionAttempt.depositStatus = toDepositStatusAttemptValue(
      data['{{deposit_status}}'],
      adoptionAttempt.depositStatus
    )

    adoptionAttempt.customerComment = data['{{asso_comment}}'] || ''

    adoptionAttempt.mockContractSentAt = isCustomContractDateFilled
      ? dayjs(customContractDate, isCustomContractDateLocalFormat ? 'DD/MM/YYYY' : undefined).toISOString()
      : ''

    adoptionAttempt.mockCertificateSentAt = isCustomCertificateDateFilled
      ? dayjs(customCertificateDate, isCustomCertificateDateLocalFormat ? 'DD/MM/YYYY' : undefined).toISOString()
      : ''

    adoptionAttempt.sterilization = {
      ...adoptionAttempt.sterilization,
      needed: true,
      depositAmount: Number(data['{{sterilization_deposit_amount}}']?.trim().replace(',', '.').replace('€', '')) || 0,
    }

    const adoptionAttempts = adopter.adoptionAttempts.map((attempt) =>
      attempt.animalId === animal.id ? adoptionAttempt : attempt
    )

    const newAdopter: AdopterDto = {
      ...adopter,
      firstName: data['{{adopter_first_name}}'] || adopter.firstName,
      lastName: data['{{adopter_last_name}}'] || adopter.lastName,
      address: data['{{adopter_address}}'] || '',
      postalCode: data['{{adopter_postal_code}}'] || '',
      city: data['{{adopter_city}}'] || adopter.city,
      email: data['{{adopter_email}}'] || adopter.email,
      phoneNumber: data['{{adopter_phone}}'] || adopter.phoneNumber,
      adoptionAttempts,
    }

    const dateOfBirth = data['{{animal_birth_date}}']

    const isBirthdayFilled = dateOfBirth != null && dateOfBirth !== ''

    const isBirthdayString = typeof dateOfBirth === 'string'

    const isLocalFormat = isBirthdayString && dateOfBirth.includes('/')

    const newAnimal: AnimalDto = {
      ...animal,
      name: data['{{animal_name}}'] || animal.name,
      sex: data['{{animal_gender}}'] as AnimalDtoSex,
      birthday: {
        date: isBirthdayFilled
          ? dayjs(dateOfBirth, isLocalFormat ? 'DD/MM/YYYY' : undefined).toISOString()
          : animal.birthday?.date || '',
      },
      color: data['{{animal_color}}'] || animal.color,
      identificationNumber: data['{{animal_identification_number}}'] || animal.identificationNumber,
      species: (data['{{animal_species}}'] || animal.species) as AnimalDtoSpecies,
      breedData: {
        ...animal.breedData,
        breedTitle: animal.breedData?.breedTitle || 'breed-type',
        breed: data['{{animal_breed}}'] || animal.breedData?.breed || '',
      },
      adoption: {
        ...animal.adoption,
        futureAdoptionDate: data['{{adoption_date}}'] || '',
        priceInEuros: isFreeAmount
          ? animal.adoption.priceInEuros
          : Number(data['{{adoption_fee}}']?.trim().replace(',', '.').replace('€', '')) || 0,
        offerType: data['{{adoption_type}}'] as AnimalAdoptionDtoOfferType,
        newName: data['{{animal_new_name}}'] || '',
      },
      vaccination: {
        ...animal.vaccination,
        vaccinationStatus: data['{{vaccine_status}}'] as CreateAnimalBodyDtoVaccinationStatus,
      },
      identifyingMarks: data['{{identifying_marks}}']?.split(',').map((mark) => mark.trim()) || [],
    }

    setMissingData([])
    resetVariableForm(
      materializeTemplateVariableFormValues(
        templateVariableKeys,
        normalizeVariableFormValues({
          ...data,
          '{{adopter_address}}': newAdopter.address || undefined,
          '{{adopter_city}}': newAdopter.city || undefined,
          '{{adopter_email}}': newAdopter.email || undefined,
          '{{adopter_first_name}}': newAdopter.firstName || undefined,
          '{{adopter_last_name}}': newAdopter.lastName || undefined,
          '{{adopter_phone}}': newAdopter.phoneNumber || undefined,
          '{{adopter_postal_code}}': newAdopter.postalCode || undefined,
          '{{adoption_date}}': data['{{adoption_date}}'] || newAnimal.adoption?.futureAdoptionDate || undefined,
          '{{adoption_fee}}': newAnimal.adoption?.priceInEuros?.toString() || undefined,
          '{{adoption_type}}': data['{{adoption_type}}'] || newAnimal.adoption?.offerType || undefined,
          '{{animal_birth_date}}': data['{{animal_birth_date}}'] || newAnimal.birthday?.date || undefined,
          '{{animal_breed}}': data['{{animal_breed}}'] || newAnimal.breedData?.breed || undefined,
          '{{animal_gender}}': data['{{animal_gender}}'] || newAnimal.sex || undefined,
          '{{animal_identification_number}}':
            data['{{animal_identification_number}}'] || newAnimal.identificationNumber || undefined,
          '{{animal_name}}': data['{{animal_name}}'] || newAnimal.name || undefined,
          '{{animal_color}}': data['{{animal_color}}'] || newAnimal.color || undefined,
          '{{animal_new_name}}': data['{{animal_new_name}}'] || newAnimal.adoption?.newName || undefined,
          '{{deposit_amount}}': data['{{deposit_amount}}'] || undefined,
          '{{deposit_date}}': data['{{deposit_date}}'] || undefined,
          '{{deposit_status}}': toDepositStatusFieldValue(data['{{deposit_status}}'], adoptionAttempt.depositStatus),
          '{{vaccine_status}}': data['{{vaccine_status}}'] || newAnimal.vaccination?.vaccinationStatus,
          '{{identifying_marks}}': data['{{identifying_marks}}'] || newAnimal.identifyingMarks?.join(', ') || undefined,
          '{{animal_species}}': data['{{animal_species}}'] || newAnimal.species || undefined,
          '{{sterilization_deposit_amount}}': data['{{sterilization_deposit_amount}}'] || undefined,
        })
      )
    )
    setFileUrl('')

    acceptedFiles.length = 0

    await AdoptersClient.editAccount(newAdopter)
    await AnimalsClient.editAnimal(newAnimal)

    const pdfTemplate = association?.pdfTemplates?.find((template) => template.name === getValues('templateKey'))
    if (pdfTemplate) {
      if (pdfTemplate.variables && pdfTemplate.variables.length > 0) {
        await fillPdfTemplate(pdfTemplate.id)
        setLoading(false)
        return
      }
    }

    const documentKey =
      association?.templates?.find((template) => template.name === getValues('templateKey'))?.documentKey ||
      'C-1002-H2lmUwJMPpx3otc6zYjlX.html'
    if (documentKey) await convertHtmlToPdf(documentKey)
    setLoading(false)
  }

  const {
    control: variableControl,
    handleSubmit: handleVariableSubmit,
    reset: resetVariableForm,
    getValues: getVariableFormValues,
    watch: watchVariableForm,
    register: registerVariableForm,
    formState: { isDirty },
  } = useForm<VariableFormType>({
    defaultValues: {
      '{{adopter_address}}': adopter.address || undefined,
      '{{adopter_city}}': adopter.city || undefined,
      '{{adopter_email}}': adopter.email || undefined,
      '{{adopter_first_name}}': adopter.firstName || undefined,
      '{{adopter_last_name}}': adopter.lastName || undefined,
      '{{adopter_phone}}': adopter.phoneNumber || undefined,
      '{{adopter_postal_code}}': adopter.postalCode || undefined,
      '{{adoption_date}}': animal?.adoption?.futureAdoptionDate || undefined,
      '{{adoption_fee}}': animal?.adoption?.priceInEuros?.toString() || undefined,
      '{{adoption_type}}': animal?.adoption?.offerType || undefined,
      '{{animal_birth_date}}': animal?.birthday?.date || undefined,
      '{{animal_breed}}': animal?.breedData?.breed || undefined,
      '{{animal_color}}': animal.color || undefined,
      '{{animal_gender}}': animal.sex || undefined,
      '{{animal_identification_number}}': animal?.identificationNumber || undefined,
      '{{animal_name}}': animal.name || undefined,
      '{{animal_new_name}}': animal?.adoption?.newName || undefined,
      '{{deposit_amount}}': adoptionAttempt?.depositAmount?.toString() || undefined,
      '{{deposit_date}}': adoptionAttempt?.depositDate || undefined,
      '{{deposit_status}}': toDepositStatusFieldValue(undefined, adoptionAttempt?.depositStatus),
      '{{current_date}}': undefined,
      '{{vaccine_recall_date}}': undefined,
      '{{vaccine_status}}': animal?.vaccination?.vaccinationStatus || undefined,
      '{{sterilization_date}}': undefined,
      '{{deworming_recall_date}}': undefined,
      '{{flea_control_recall_date}}': undefined,
      '{{identifying_marks}}': animal?.identifyingMarks?.join(', ') || undefined,
      '{{animal_species}}': animal?.species || undefined,
      '{{asso_name}}': undefined,
      '{{asso_address}}': undefined,
      '{{asso_postal_code}}': undefined,
      '{{asso_city}}': undefined,
      '{{asso_siret_number}}': undefined,
      '{{president_last_name}}': undefined,
      '{{president_first_name}}': undefined,
      '{{asso_logo}}': undefined,
      '{{asso_signature}}': undefined,
      '{{sterilization_deposit_amount}}': undefined,
      '{{date_7_days}}': undefined,
      '{{custom_contract_date}}': undefined,
      '{{custom_certificate_date}}': undefined,
      '{{vaccine_last_date}}': undefined,
      '{{deworming_last_date}}': undefined,
      '{{flea_control_last_date}}': undefined,
      '{{family_firstName}}': undefined,
      '{{family_lastName}}': undefined,
    },
  })

  interface formType {
    templateKey: string
  }

  const { control, handleSubmit, getValues, watch } = useForm<formType>()

  const species = accountsStore.connectedCustomer?.preferences.species
  const watchTemplateKey = watch('templateKey')

  const adoptionFee = watchVariableForm('{{adoption_fee}}')
  const isFreeAmount = animal.adoption.offerType === AnimalAdoptionDtoOfferType.FreeAmount
  const selectedPdfTemplate = React.useMemo(() => {
    const templateType = type === 'certificate' ? 'knowledge-certificate-model' : 'adoption-contract-model'

    return association?.pdfTemplates?.find(
      (template) => template.type === templateType && template.name === watchTemplateKey
    )
  }, [association?.pdfTemplates, type, watchTemplateKey])

  const selectedTemplateVariableKeys = React.useMemo(() => {
    return getDeclaredTemplateVariableKeys([selectedPdfTemplate])
  }, [selectedPdfTemplate])

  const templateVariableKeys = React.useMemo(() => {
    return getTemplateVariableKeys(allVariables, missingData, selectedTemplateVariableKeys)
  }, [allVariables, missingData, selectedTemplateVariableKeys])

  useEffect(() => {
    if (templateVariableKeys.length > 0) {
      const normalizedAllVariables = normalizeVariableFormValues(allVariables ?? {})
      const currentValues = getVariableFormValues()
      const mergedValues = mergeDefinedVariableFormValues(currentValues, normalizedAllVariables)
      const materializedValues = materializeTemplateVariableFormValues(templateVariableKeys, mergedValues)
      if (!templateVariableKeys.includes('{{adoption_fee}}')) {
        return resetVariableForm({
          ...materializedValues,
          '{{adoption_fee}}': currentValues['{{adoption_fee}}'] ?? adoptionFee,
        })
      } else {
        return resetVariableForm({
          ...materializedValues,
          '{{adoption_fee}}': normalizedAllVariables['{{adoption_fee}}']
            ? normalizedAllVariables['{{adoption_fee}}'].trim().replace(',', '.').replace('€', '')
            : '0',
        })
      }
    }
  }, [adoptionFee, allVariables, getVariableFormValues, resetVariableForm, templateVariableKeys])

  const templateType = type === 'certificate' ? 'knowledge-certificate-model' : 'adoption-contract-model'
  const petsoTemplateOptions =
    type === 'contract'
      ? ['Contrat généré par Petso']
      : specieThatHasCertificateGeneration
      ? ['Certificat généré par Petso']
      : []

  const options = [
    {
      title: "Modèles PDF de l'asso",
      options: association?.pdfTemplates
        ? association?.pdfTemplates
            ?.filter((template) => template.type === templateType)
            .map((template) => template.name)
        : ['Pas encore de modèle PDF dans votre asso'],
    },
    /* {
      title: "Modèles de l'asso",
      options: association?.templates
        ? association?.templates?.filter((template) => template.type === templateType).map((template) => template.name)
        : ['Pas encore de modèles dans votre asso'],
    }, */
    {
      title: 'Modèle Petso',
      options: petsoTemplateOptions,
    },
    {
      title: 'Import ponctuel (pdf)',
      options: ['Importer un document PDF'],
    },
  ]

  const breedOptions =
    animal.species === 'cat'
      ? catBreeds.map((breed) => ({ label: breed, value: breed }))
      : dogBreeds.map((breed) => ({ label: breed, value: breed }))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const openDialogWithRightStep = () => {
    setStep(manualImportMode ? 1 : 0)
    openDialog()
  }

  return (
    <>
      <Tooltip
        title={
          type === 'contract' && disabled === true
            ? `Vous devez valider les informations de paiement avant de pouvoir envoyer le contrat`
            : ''
        }
      >
        <span style={{ width: isSmallScreen ? '100%' : 'auto' }}>
          <Button
            variant={manualImportMode ? 'outlined' : 'contained'}
            fullWidth={isSmallScreen}
            sx={{
              ...(manualImportMode ? addSecondaryFabSx : addPrimaryFabSx),
              fontSize: { xs: '14px', sm: '16px' },
              minHeight: 36,
              height: 36,
              px: { xs: 1.75, sm: 2 },
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: 'nowrap',
            }}
            onClick={openDialogWithRightStep}
            disabled={type === 'contract' && disabled === true}
          >
            {buttonLabel ||
              (isLocked
                ? `Préparer le ${type === 'certificate' ? 'certificat' : 'contrat'}`
                : `Envoyer le ${type === 'certificate' ? 'certificat' : 'contrat'}`)}
          </Button>
        </span>
      </Tooltip>
      <Dialog
        open={isDialogOpen}
        onClose={() => resetAndCloseDialog()}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
        sx={{ width: { xs: 'auto', sm: '500px', md: 700 }, mx: 'auto' }}
      >
        <DialogContent
          sx={{
            padding: 0,
          }}
        >
          {loading && watchTemplateKey !== 'Importer un document PDF' && (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" padding={3}>
              <Typography variant="h4" component="h2" sx={{ fontSize: '20px', color: blue }}>
                Génération du document...
              </Typography>
              <CircularProgress sx={{ marginTop: 2 }} />
            </Box>
          )}
          {!manualImportMode && step === 0 ? (
            <Box>
              {!loading && (
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontSize: { xs: '18px', sm: '20px' },
                    color: blue,
                    pl: { xs: 0.5, sm: 0.75 },
                    pt: { xs: 0.35, sm: 0.5 },
                    mb: { xs: 2, sm: 2.5 },
                  }}
                >
                  {`Emission du ${type === 'certificate' ? 'certificat de connaissance' : "contrat d'adoption"}`}
                </Typography>
              )}

              <Box
                sx={{
                  mb: 3,
                  px: 1.6,
                  py: 1.2,
                  border: '1px solid rgba(105, 141, 172, 0.18)',
                  borderRadius: 2,
                  background: 'rgba(232, 242, 251, 0.92)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Info sx={{ color: '#698DAC', fontSize: 18, mt: '1px', flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#203864',
                    fontSize: { xs: '13.5px', sm: '14px' },
                    lineHeight: 1.4,
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    Pas encore de modèle ?
                  </Box>{' '}
                  pour envoyer en un clic vos propres certificats et contrats,{' '}
                  <Link to="/association/template">créez et enregistrez vos modèles</Link>.
                </Typography>
              </Box>
              <form id="templateForm" onSubmit={handleSubmit(onSubmit)}>
                <ControlledAutoCompleteWithCategories
                  control={control}
                  error={
                    watchTemplateKey === 'Pas encore de modèles dans votre asso'
                      ? { type: 'value', message: 'Champ requis' }
                      : undefined
                  }
                  label="Choisir un modèle"
                  fieldName="templateKey"
                  options={options.filter((option) => option.options.length > 0)}
                  disabled={loading}
                  sx={{ marginBottom: 2 }}
                  categoryColor={theme.palette.secondary.main}
                />
              </form>
            </Box>
          ) : (
            // STEP 1: UPLOAD CERTIFICATE / CONTRACT
            <Box>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontSize: { xs: '18px', sm: '20px' },
                  color: blue,
                  fontWeight: { xs: 700, sm: 'normal' },
                  textAlign: { xs: 'center', sm: 'left' },
                  pl: { xs: 0, sm: 0.75 },
                  pt: { xs: 0.2, sm: 0.35 },
                  mb: { xs: 3, sm: 4 },
                }}
              >
                {!loading &&
                  (choosedPetsoDocument
                    ? `Votre ${
                        type === 'certificate' ? 'certificat de connaissance' : "contrat d'adoption"
                      } est prêt pour envoi`
                    : `Importer votre propre ${
                        type === 'certificate' ? 'certificat de connaissance' : "contrat d'adoption"
                      } ${manualImportMode ? 'pour compléter le dossier' : 'pour signature'}`)}
              </Typography>
              {missingData.length > 0 && (
                <Box marginBottom={2}>
                  <InfoBox
                    content="Un ou plusieurs champs utilisés dans le modèle sont manquants !"
                    messageType="error"
                  />
                </Box>
              )}
              {choosedPetsoDocument ? (
                <Box
                  sx={{
                    mb: 2,
                    px: 2,
                    py: 1.35,
                    border: '1px solid rgba(105, 141, 172, 0.18)',
                    borderRadius: 2,
                    background: 'rgba(232, 242, 251, 0.92)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.55,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#698DAC',
                          fontWeight: 700,
                          fontSize: { xs: 13.5, sm: 14 },
                        }}
                      >
                        Aperçu du document
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.cello,
                          fontSize: { xs: '13px', sm: '13.5px' },
                          fontStyle: 'italic',
                        }}
                      >
                        Vérifiez le rendu avant l&apos;envoi.
                      </Typography>
                    </Box>
                    <Box
                      component="a"
                      href={fileUrl ?? '#'}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        alignSelf: { xs: 'flex-start', sm: 'center' },
                        minHeight: 36,
                        px: 1.75,
                        borderRadius: '999px',
                        border: '1px solid rgba(122, 129, 151, 0.22)',
                        backgroundColor: '#F4F6FA',
                        color: colors.cello,
                        fontSize: { xs: '13.5px', sm: '14px' },
                        fontWeight: 700,
                        textDecoration: 'none',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        '&:hover': {
                          textDecoration: 'none',
                          backgroundColor: '#EDF1F6',
                          borderColor: 'rgba(122, 129, 151, 0.3)',
                        },
                      }}
                    >
                      Consulter
                    </Box>
                  </Box>
                </Box>
              ) : isSmallScreen ? (
                <Box {...getRootProps()} width="100%">
                  <input {...getInputProps()} />
                  <Box>
                    <Typography fontSize="16px" textAlign="center">
                      {acceptedFiles[0]?.name || 'Aucun fichier sélectionné'}
                    </Typography>
                  </Box>
                  <Button variant="contained" sx={{ width: '100%', mt: 2, textTransform: 'none' }}>
                    Parcourir
                  </Button>
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  padding={3}
                  {...getRootProps({
                    sx: {
                      ...spacingItem,
                      border: 3,
                      borderRadius: 5,
                      borderStyle: 'dashed',
                      borderColor: isDragActive ? 'green' : blue,
                      backgroundColor: isDragActive
                        ? 'lightgreen'
                        : acceptedFiles[0]
                        ? setOpacity(colors.geyser, 0.4)
                        : 'transparent',
                      transition: 'all 0.3s',
                    },
                  })}
                >
                  <input {...getInputProps()} />
                  <UploadFile sx={{ color: blue, fontSize: 64, mb: 1 }} />
                  <Typography variant="h4" component="p" fontSize={20} textAlign="center">
                    Glissez-déposer ou
                    <br />
                    {manualImportMode ? 'Insérer un document' : 'Insérer un document PDF'}
                  </Typography>
                  <Box
                    marginTop={2}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 1,
                      maxWidth: '100%',
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="p"
                      fontSize={18}
                      textAlign="center"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {manualImportMode ? 'Votre document:' : 'Votre document PDF:'}
                    </Typography>
                    <Typography
                      variant="h4"
                      component="p"
                      fontSize={18}
                      textAlign="center"
                      color={colors.black}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                      }}
                    >
                      {acceptedFiles[0]?.name || 'Aucun fichier sélectionné'}
                    </Typography>
                  </Box>
                  <Button variant="contained" sx={{ textTransform: 'none', fontSize: '16px', width: '40%', mt: 2 }}>
                    Parcourir
                  </Button>
                </Box>
              )}
              {!manualImportMode && !adoptionFee && (
                <Box marginBottom={2}>
                  <InfoBox
                    content="Le montant de l'adoption n'a pas été renseigné, veuillez le renseigner pour continuer."
                    messageType="error"
                  />
                </Box>
              )}
              {!manualImportMode && (templateVariableKeys.length > 0 || !animal?.adoption?.priceInEuros) && (
                <form id="variableForm" onSubmit={handleVariableSubmit(onSubmitVariableForm)}>
                  <Box marginBottom={2}>
                    <Typography variant="h4" component="h2" sx={{ fontSize: '18px', color: 'black' }}>
                      {`Variables utilisées dans le modèle:`}
                    </Typography>
                    <TextField
                      id="price-input"
                      label={isFreeAmount ? "Montant minimum de l'adoption" : "Montant de l'adoption"}
                      inputProps={{
                        ...(isFreeAmount
                          ? { readOnly: true }
                          : {
                              step: '0.01',
                              inputMode: 'decimal',
                              pattern: '[0-9]*',
                            }),
                      }}
                      fullWidth
                      {...registerVariableForm('{{adoption_fee}}', { required: true })}
                      InputProps={
                        isFreeAmount ? undefined : { endAdornment: <InputAdornment position="end">€</InputAdornment> }
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={sxField}
                    />
                    {templateVariableKeys.length > 0 &&
                      templateVariableKeys.map((key) => {
                        if (key === '{{adoption_fee}}' || key.includes('logo') || key.includes('signature')) {
                          return null
                        }
                        if (key === '{{deposit_amount}}') {
                          return (
                            <TextField
                              id="price-input"
                              key={key}
                              label="Montant de l'acompte"
                              inputProps={{
                                step: '0.01',
                                inputMode: 'decimal',
                                pattern: '[0-9]*',
                              }}
                              fullWidth
                              {...registerVariableForm('{{deposit_amount}}', { required: true })}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                              }}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              sx={sxField}
                            />
                          )
                        }
                        if (key === '{{sterilization_deposit_amount}}') {
                          return (
                            <TextField
                              id="price-input"
                              key={key}
                              label="Montant de la caution de stérilisation"
                              inputProps={{
                                step: '0.01',
                                inputMode: 'decimal',
                                pattern: '[0-9]*',
                              }}
                              fullWidth
                              {...registerVariableForm('{{sterilization_deposit_amount}}', { required: true })}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                              }}
                              InputLabelProps={{ shrink: true }}
                              size="small"
                              sx={sxField}
                            />
                          )
                        }
                        if (key === '{{deposit_status}}') {
                          return (
                            <ColoredChipSelectField
                              control={variableControl}
                              key={key}
                              error={undefined}
                              fieldName="{{deposit_status}}"
                              label="État de l'acompte"
                              options={[
                                { label: 'Validé', value: 'true', color: colors.seaGreen },
                                { label: 'En attente', value: 'false', color: colors.amber },
                              ]}
                              requiredRule=""
                              sx={{ marginTop: 2 }}
                              size="small"
                            />
                          )
                        }
                        if (key.includes('species')) {
                          return (
                            <ControlledSelectField
                              key={key}
                              sx={spacingItem}
                              control={variableControl}
                              error={undefined}
                              fieldName="{{animal_species}}"
                              label="Espèce"
                              options={
                                species
                                  ? Object.values(species).map((status: AnimalDtoSpecies) => ({
                                      label: getReadableSpecies(status, { withEmoji: true }),
                                      value: status,
                                    }))
                                  : []
                              }
                              requiredRule="L'espèce est requise."
                              disabled={species?.length === 1}
                            />
                          )
                        }
                        if (key.includes('date')) {
                          return (
                            <ControlledDateField
                              key={key}
                              control={variableControl}
                              validate={(value) => isValidPastDateCoherence(value) || value === null}
                              error={undefined}
                              fieldName={key as keyof VariableFormType}
                              label={translateVariablePlaceholder(key)}
                              requiredRule={undefined}
                              sx={{
                                ...sxField,
                                backgroundColor: missingData.includes(key) ? setOpacity(colors.red, 0.5) : 'inherit',
                              }}
                              disabled={key.includes('recall') || key.includes('sterilization')}
                              helperText={
                                key.includes('recall') || key.includes('sterilization')
                                  ? `Champ modifiable uniquement via la fiche de l'animal`
                                  : ''
                              }
                              size="small"
                            />
                          )
                        }
                        if (key.includes('phone')) {
                          return (
                            <FormControl
                              fullWidth
                              key={key}
                              variant="outlined"
                              sx={{
                                ...sxField,
                              }}
                            >
                              <Controller
                                name={key as keyof VariableFormType}
                                control={variableControl}
                                rules={{
                                  validate: (value: string | undefined) => {
                                    return !value ||
                                      value.trim() === '' ||
                                      matchIsValidTel(value) ||
                                      value.trim() === '+33'
                                      ? true
                                      : 'Tel is invalid'
                                  },
                                }}
                                render={({ field, fieldState }) => (
                                  <MuiTelInput
                                    {...field}
                                    label="Numero de Téléphone"
                                    defaultCountry="FR"
                                    helperText={fieldState.error ? "Le numero de téléphone n'est pas valide" : ''}
                                    error={!!fieldState.error}
                                    sx={{
                                      backgroundColor: missingData.includes(key)
                                        ? setOpacity(colors.red, 0.5)
                                        : 'inherit',
                                    }}
                                    size="small"
                                  />
                                )}
                              />
                            </FormControl>
                          )
                        }
                        if (
                          animal &&
                          key.includes('breed') &&
                          (animal?.species === 'dog' || animal?.species === 'cat')
                        ) {
                          return (
                            <ControlledAutoCompleteWithCustomValue
                              key={key}
                              control={variableControl}
                              fieldName={key as keyof VariableFormType}
                              error={undefined}
                              aria-label="Race"
                              label="Race"
                              requiredRule="La race est requise"
                              options={breedOptions}
                              defaultValue=""
                              size="small"
                              freeSolo={animal?.species === 'dog'}
                              helperText={
                                animal?.species === 'dog'
                                  ? `Si la race n'est pas dans les propositions, écrivez là et appuyez sur la touche entrée`
                                  : ''
                              }
                              sx={{
                                ...sxField,
                                backgroundColor: missingData.includes(key) ? setOpacity(colors.red, 0.5) : 'inherit',
                              }}
                            />
                          )
                        }
                        if (key.includes('adoption_type')) {
                          return (
                            <ControlledSelectField
                              key={key}
                              control={variableControl}
                              error={undefined}
                              fieldName={key as keyof VariableFormType}
                              label={translateVariablePlaceholder(key)}
                              options={animalAdoptionOfferTypeOrder.map((field: AnimalAdoptionDtoOfferType) => ({
                                label: getReadableAnimalAdoptionOfferType(field),
                                value: field,
                              }))}
                              requiredRule=""
                              size="small"
                              sx={{
                                ...sxField,
                                backgroundColor: missingData.includes(key) ? setOpacity(colors.red, 0.5) : 'inherit',
                              }}
                            />
                          )
                        }
                        if (key.includes('gender')) {
                          return (
                            <ControlledSelectField
                              key={key}
                              sx={{
                                ...sxField,
                                backgroundColor: missingData.includes(key) ? setOpacity(colors.red, 0.5) : 'inherit',
                              }}
                              control={variableControl}
                              error={undefined}
                              fieldName={key as keyof VariableFormType}
                              label={translateVariablePlaceholder(key)}
                              options={[
                                { label: 'Mâle', value: 'male' },
                                { label: 'Femelle', value: 'female' },
                              ]}
                              requiredRule="Le sexe est requis"
                              size="small"
                            />
                          )
                        }
                        if (key.includes('vaccine_status')) {
                          return (
                            <ColoredChipSelectField
                              key={key}
                              control={variableControl}
                              error={undefined}
                              fieldName={key as keyof VariableFormType}
                              size="small"
                              label="Statut primovaccination"
                              options={Object.values(CreateAnimalBodyDtoVaccinationStatus).map(
                                (status: CreateAnimalBodyDtoVaccinationStatus) => ({
                                  label: getReadableAnimalVaccineStatus(status),
                                  value: status,
                                  color: getColorForAnimalVaccineStatus(status),
                                })
                              )}
                              sx={{
                                ...sxField,
                                backgroundColor: missingData.includes(key) ? setOpacity(colors.red, 0.5) : 'inherit',
                              }}
                              requiredRule="Le statut primovaccination est requis"
                            />
                          )
                        }
                        return (
                          <Controller
                            key={key}
                            name={key as keyof VariableFormType}
                            control={variableControl}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={translateVariablePlaceholder(key)}
                                fullWidth
                                sx={{
                                  ...sxField,
                                  backgroundColor: missingData.includes(key) ? setOpacity(colors.red, 0.5) : 'inherit',
                                }}
                                size="small"
                                disabled={
                                  (key.includes('asso') && !key.includes('comment')) || key.includes('president')
                                }
                                helperText={
                                  (key.includes('asso') && !key.includes('comment')) || key.includes('president')
                                    ? `Champ modifiable uniquement via la fiche de l'asso`
                                    : ''
                                }
                              />
                            )}
                          />
                        )
                      })}
                  </Box>
                </form>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 0, sm: 'auto' }, pt: { xs: 1, sm: 2 } }}>
          {!manualImportMode && step === 0 ? (
            <>
              <LoadingButton
                variant="contained"
                type="submit"
                form="templateForm"
                loading={loading}
                sx={{
                  textTransform: 'none',
                  fontSize: { xs: '14px', sm: '16px' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {`Suivant`}
              </LoadingButton>
            </>
          ) : (
            <>
              <Button
                data-testid="cancel"
                onClick={() => resetAndCloseDialog()}
                sx={{
                  textTransform: 'none',
                  fontSize: { xs: '14px', sm: '16px' },
                  width: { xs: '50%', sm: 'auto' },
                  px: { xs: 'auto', sm: '16px' },
                }}
              >
                Annuler
              </Button>
              {isDirty ? (
                <LoadingButton
                  data-testid="apply-changes"
                  variant="contained"
                  form="variableForm"
                  type="submit"
                  loading={loading}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '14px', sm: '16px' },
                    width: { xs: '50%', sm: 'auto' },
                    px: { xs: 'auto', sm: '16px' },
                  }}
                >
                  Enregistrer les modifications
                </LoadingButton>
              ) : (
                <LoadingButton
                  data-testid="apply-changes"
                  variant="contained"
                  type="submit"
                  loading={mutation.isLoading}
                  onClick={() => onSubmit()}
                  sx={{
                    textTransform: 'none',
                    fontSize: { xs: '14px', sm: '16px' },
                    width: { xs: '50%', sm: 'auto' },
                    px: { xs: 'auto', sm: '16px' },
                  }}
                >
                  {missingData.length > 0 ? 'Valider quand même' : 'Valider'}
                </LoadingButton>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
